import useUiTranslation from "../utils/useUiTranslation";
import { openProtectedFile } from '../api/attachments';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/axios';
import { hasPermission } from '../utils/permissions';
import useCurrentUser from '../hooks/useCurrentUser';
import PageShell from '../components/layout/PageShell';
import AmountInput from '../components/ui/AmountInput';

const ProcurementPlansPage = () => {
  const uiText = useUiTranslation();
  const { user } = useCurrentUser();
  const canManage = hasPermission(user,'planning.manage');
  const [plans, setPlans] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterYear, setFilterYear] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [planItems, setPlanItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState('');
  const [dataLimitations,setDataLimitations] = useState([]);
  const [integrationInsights, setIntegrationInsights] = useState([]);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [itemForm, setItemForm] = useState({
    item_name: '',
    planned_quantity: '',
    planned_unit_cost: '',
    unit_of_measure: '',
    currency: 'USD',
    needed_by_date: '',
  });
  const fileInputRef = useRef(null);

  const fetchPlans = useCallback(
    async (options = {}) => {
      if (!user) return;

      const params = {
        department_id: user.department_id,
        ...(options.year && options.year !== 'all' ? { year: options.year } : {}),
      };

      try {
        setIsFetching(true);
        setError('');
        const res = await api.get('/procurement-plans', { params });
        setPlans(res.data || []);
      } catch (err) {
        console.error('Failed to load plans', err);
        setError('We were unable to load the procurement plans. Please try again.');
      } finally {
        setIsFetching(false);
      }
    },
    [user]
  );

  useEffect(() => {
    fetchPlans({ year: filterYear });
  }, [fetchPlans, filterYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Select a file to upload.');
      setSuccess('');
      return;
    }
    const formData = new FormData();
    formData.append('plan', file);
    formData.append('plan_year', year);
    formData.append('department_id', user.department_id);
    try {
      setLoading(true);
      setSuccess('');
      setError('');
      await api.post('/procurement-plans', formData);
      setFile(null);
      setSuccess('Procurement plan uploaded successfully.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchPlans({ year: filterYear });
    } catch (err) {
      console.error('Upload failed', err);
      setError('Failed to upload plan. Please check the file and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanItems = useCallback(
    async (planId) => {
      if (!planId) {
        setPlanItems([]);
        return;
      }

      try {
        setIsLoadingItems(true);
        setIsLoadingInsights(true);
        setItemsError('');
        const [varianceRes, insightsRes] = await Promise.all([
          api.get(`/procurement-plans/${planId}/items/variance`),
          api.get(`/procurement-plans/${planId}/integration-insights`),
        ]);
        setPlanItems(varianceRes.data || []);
        setIntegrationInsights(insightsRes.data?.items || []);
        setDataLimitations(insightsRes.data?.data_limitations || []);
      } catch (err) {
        console.error('Failed to load plan items', err);
        setItemsError('Unable to load plan line items. Please try again.');
      } finally {
        setIsLoadingItems(false);
        setIsLoadingInsights(false);
      }
    },
    []
  );

  const handlePlanSelection = (value) => {
    setSelectedPlanId(value);
    fetchPlanItems(value);
  };

  const handleItemFieldChange = (field, value) => {
    setItemForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetItemForm = () => {
    setItemForm({
      item_name: '',
      planned_quantity: '',
      planned_unit_cost: '',
      unit_of_measure: '',
      currency: 'USD',
      needed_by_date: '',
    });
  };

  const handleItemSubmit = async (event) => {
    event.preventDefault();
    if (!selectedPlanId) {
      setItemsError('Select a plan before adding items.');
      return;
    }

    const payload = {
      items: [
        {
          item_name: itemForm.item_name.trim(),
          planned_quantity: itemForm.planned_quantity,
          planned_unit_cost: itemForm.planned_unit_cost || undefined,
          unit_of_measure: itemForm.unit_of_measure || undefined,
          currency: itemForm.currency || undefined,
          needed_by_date: itemForm.needed_by_date || undefined,
        },
      ],
    };

    try {
      setItemsError('');
      await api.post(`/procurement-plans/${selectedPlanId}/items`, payload);
      resetItemForm();
      fetchPlanItems(selectedPlanId);
    } catch (err) {
      console.error('Failed to add plan item', err);
      setItemsError('Unable to add plan item. Check the form and try again.');
    }
  };

  const availableYears = useMemo(() => {
    const years = new Set(plans.map((p) => p.plan_year));
    return Array.from(years).sort((a, b) => b - a);
  }, [plans]);

  const filteredPlans = useMemo(() => {
    return plans
      .filter((plan) =>
        filterYear === 'all' ? true : Number(plan.plan_year) === Number(filterYear)
      )
      .filter((plan) =>
        searchTerm
          ? plan.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(plan.plan_year).includes(searchTerm)
          : true
      )
      .sort((a, b) => Number(b.plan_year) - Number(a.plan_year));
  }, [plans, filterYear, searchTerm]);

  const renderStatusMessage = () => {
    if (loading) {
      return 'Uploading procurement plan...';
    }
    if (success) {
      return success;
    }
    if (error) {
      return error;
    }
    return '';
  };

  const statusType = loading ? 'info' : success ? 'success' : error ? 'error' : '';

  const kpis = useMemo(() => {
    const total = plans.length;
    const visible = filteredPlans.length;
    const latestYear = availableYears[0] || '—';
    const totalItems = planItems.length;
    return [
      { label: 'Plans total', value: total },
      { label: 'Plans in view', value: visible },
      { label: 'Latest plan year', value: latestYear },
      { label: 'Line items loaded', value: totalItems },
    ];
  }, [plans.length, filteredPlans.length, availableYears, planItems.length]);

  const planTable = (
    <div className="overflow-x-auto rounded border border-gray-200 shadow-sm bg-white">
      <table className="min-w-full divide-y divide-gray-200" aria-label="Procurement plans table">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Year
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              File
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Uploaded On
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{uiText("Actions")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {filteredPlans.map((plan) => {
            const uploadedOn = plan.created_at
              ? new Date(plan.created_at).toLocaleString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : '—';

            const downloadUrl = plan.download_url || (plan.file_path ? `/${plan.file_path}` : '#');
            const canDownload = Boolean(plan.download_url || plan.file_path);

            return (
              <tr key={plan.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{plan.plan_year}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{plan.file_name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{uploadedOn}</td>
                <td className="px-4 py-3 text-sm">
                  {canDownload ? (
                    <button
                      type="button"
                      onClick={() => openProtectedFile(downloadUrl, plan.file_name || 'plan', true).catch(() => setError('Unable to download plan'))}
                      className="text-blue-600 hover:text-blue-700 font-semibold"
                    >{uiText("View")}</button>
                  ) : (
                    <span className="text-gray-400">Unavailable</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {filteredPlans.length === 0 && (
        <div className="px-4 py-6 text-sm text-gray-500 text-center">
          No procurement plans match the selected filters.
        </div>
      )}
    </div>
  );

  const integrationTable = (
    <div className="overflow-x-auto rounded border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200" aria-label="Integrated planning insights table">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{uiText("Item")}</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Demand/mo</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{uiText("Lead time (days)")}</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Seasonal index</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Recommended action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {integrationInsights.map((item) => (
            <tr key={item.plan_item_id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-700">{item.item_name}</td>
              <td className="px-4 py-3 text-sm text-right text-gray-700">{item.demand_monthly_avg ?? 0}</td>
              <td className="px-4 py-3 text-sm text-right text-gray-700">{item.lead_time_days ?? 0}</td>
              <td className="px-4 py-3 text-sm text-right text-gray-700">{item.seasonal_index ?? 1}</td>
              <td className="px-4 py-3 text-sm text-gray-700">{(item.recommended_actions || []).join(' ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {integrationInsights.length === 0 && !isLoadingInsights && (
        <div className="px-4 py-6 text-center text-sm text-gray-500">
          No integration insights available for this plan yet.
        </div>
      )}
    </div>
  );

  const varianceTable = (
    <div className="overflow-x-auto rounded border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200" aria-label="Plan item variance table">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{uiText("Item")}</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Planned Qty
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{uiText("Requested Qty")}</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Consumed Qty
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Request Variance
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Consumption Variance
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {planItems.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-700">{item.item_name}</td>
              <td className="px-4 py-3 text-sm text-right text-gray-700">{item.planned_quantity}</td>
              <td className="px-4 py-3 text-sm text-right text-gray-700">{item.requested_quantity}</td>
              <td className="px-4 py-3 text-sm text-right text-gray-700">{item.consumed_quantity}</td>
              <td className="px-4 py-3 text-sm text-right text-gray-700">
                {item.request_quantity_variance}
              </td>
              <td className="px-4 py-3 text-sm text-right text-gray-700">
                {item.consumption_quantity_variance}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {planItems.length === 0 && !isLoadingItems && (
        <div className="px-4 py-6 text-center text-sm text-gray-500">
          No structured plan items found for this plan yet.
        </div>
      )}
    </div>
  );

  return (
    <PageShell
      title={uiText("Procurement Plans")}
      description="Upload and review annual procurement plans for your department. Use the filters to quickly find historical plans and confirm compliance."
      actions={
        <button
          type="button"
          onClick={() => fetchPlans({ year: filterYear })}
          className="inline-flex items-center rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Refresh plans
        </button>
      }
      filters={
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-sm font-medium text-gray-700">Year
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="all">All years</option>
              {availableYears.map((yr) => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700 md:col-span-2">{uiText("Search")}<input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              placeholder="Search by file name or year"
            />
          </label>
        </div>
      }
      kpis={kpis}
    >
      {!canManage && <p className="text-sm text-gray-600">Read-only planning access. Plan preparation requires planning management permission.</p>}
      <section className="grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Upload a plan</h2>
            <p className="mt-1 text-sm text-gray-600">
              Accepted file types include PDF, Excel, and Word documents up to 10MB.
            </p>
            <fieldset disabled={!canManage} aria-label="Upload plan">
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="plan-year">
                  Plan year
                </label>
                <input
                  id="plan-year"
                  type="number"
                  min="2000"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  aria-describedby="plan-year-help"
                  required
                />
                <p id="plan-year-help" className="mt-1 text-xs text-gray-500">
                  Enter the calendar year that this procurement plan covers.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="plan-file">
                  Upload file
                </label>
                <input
                  id="plan-file"
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="mt-1 block w-full text-sm text-gray-700 file:mr-4 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-blue-600 hover:file:bg-blue-100"
                  required
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                disabled={loading}
              >
                {loading ? uiText("Uploading…") : 'Upload plan'}
              </button>
              {renderStatusMessage() && (
                <div
                  role="status"
                  className={`rounded border px-3 py-2 text-sm ${
                    statusType === 'info'
                      ? 'border-blue-200 bg-blue-50 text-blue-700'
                      : statusType === 'success'
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : statusType === 'error'
                      ? 'border-red-200 bg-red-50 text-red-600'
                      : 'border-gray-200 bg-gray-50 text-gray-600'
                  }`}
                >
                  {renderStatusMessage()}
                </div>
              )}
            </form>
            </fieldset>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Plans on record</h2>
            <p className="mt-1 text-sm text-gray-600">
              Browse previously uploaded procurement plans.
            </p>
            <div className="mt-4 space-y-4">
              {isFetching ? (
                <p className="text-sm text-gray-500">Loading procurement plans…</p>
              ) : (
                planTable
              )}
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Structured plan items</h2>
            <p className="mt-1 text-sm text-gray-600">
              Add line items to connect plans with requests and inventory consumption for variance tracking.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-[240px,1fr]">
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="plan-select">
                Select plan
              </label>
              <select
                id="plan-select"
                value={selectedPlanId}
                onChange={(e) => handlePlanSelection(e.target.value)}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Choose a plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.plan_year} · {plan.file_name}
                  </option>
                ))}
              </select>
            </div>

            <fieldset disabled={!canManage} aria-label="Prepare plan items">
            <form onSubmit={handleItemSubmit} className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700" htmlFor="item-name">{uiText("Item name")}</label>
                <input
                  id="item-name"
                  type="text"
                  value={itemForm.item_name}
                  onChange={(e) => handleItemFieldChange('item_name', e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="planned-quantity">
                  Planned quantity
                </label>
                <input
                  id="planned-quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  value={itemForm.planned_quantity}
                  onChange={(e) => handleItemFieldChange('planned_quantity', e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="planned-unit-cost">
                  Planned unit cost
                </label>
                <AmountInput
                  id="planned-unit-cost"
                  min="0"
                  step="0.01"
                  value={itemForm.planned_unit_cost}
                  onChange={(e) => handleItemFieldChange('planned_unit_cost', e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="unit-of-measure">{uiText("Unit of measure")}</label>
                <input
                  id="unit-of-measure"
                  type="text"
                  value={itemForm.unit_of_measure}
                  onChange={(e) => handleItemFieldChange('unit_of_measure', e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="currency">{uiText("Currency")}</label>
                <input
                  id="currency"
                  type="text"
                  value={itemForm.currency}
                  onChange={(e) => handleItemFieldChange('currency', e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="needed-by">
                  Needed by date
                </label>
                <input
                  id="needed-by"
                  type="date"
                  value={itemForm.needed_by_date}
                  onChange={(e) => handleItemFieldChange('needed_by_date', e.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Add line item
                </button>
              </div>
            </form>
            </fieldset>
          </div>

          {itemsError && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {itemsError}
            </div>
          )}

          {isLoadingItems ? <p className="text-sm text-gray-500">Loading plan items…</p> : varianceTable}

          <div className="pt-4">
            <h3 className="text-md font-semibold text-gray-900">Integrated planning insights</h3>
            {dataLimitations.map(note => <p key={note} className="text-sm text-gray-600">{note}</p>)}
            <p className="mt-1 text-sm text-gray-600">
              Connect plan items to consumption history, demand planning, reorder policy, lead time, and seasonal usage.
            </p>
            <div className="mt-3">
              {isLoadingItems ? <p className="text-sm text-gray-500">Loading insights…</p> : integrationTable}
            </div>
          </div>
        </section>
    </PageShell>
  );
};

export default ProcurementPlansPage;